import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Upload, X, Palette, CheckCircle, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const projectTypes = [
  'Logo Design',
  'Brand Identity',
  'Marketing Materials',
  'Packaging Design',
  'Signage & Panels',
  'Event Materials',
  'Advertising Campaign',
  'Restaurant / Hotel Materials',
  'Book / Publication',
  'Other',
];

const budgetRanges = [
  'Under $100',
  '$100 - $250',
  '$250 - $500',
  '$500 - $1000',
  '$1000+',
  'Not sure',
];

export function CustomDesignPage() {
  const navigate = useNavigate();
  const { user, api } = useAuth();
  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    project_type: '',
    budget_range: '',
    deadline: '',
  });
  const [inspirationImages, setInspirationImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSelectChange = (name, value) => {
    setFormData({ ...formData, [name]: value });
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    if (!user) {
      toast.error('Please login to upload images');
      navigate('/login');
      return;
    }

    setUploading(true);
    
    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        const { data } = await api.post('/api/upload', formData);
        setInspirationImages((prev) => [
          ...prev,
          { id: data.id, name: file.name, preview: URL.createObjectURL(file) },
        ]);
      } catch {
        // Upload failed
        toast.error(`Failed to upload ${file.name}`);
      }
    }

    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeImage = (id) => {
    setInspirationImages((prev) => prev.filter((img) => img.id !== id));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!user) {
      toast.error('Please login to submit a project');
      navigate('/login');
      return;
    }

    if (!formData.title || !formData.description || !formData.project_type) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSubmitting(true);

    try {
      await api.post('/api/custom-projects', {
        ...formData,
        inspiration_images: inspirationImages.map((img) => img.id),
      });

      setSubmitted(true);
      toast.success('Project submitted successfully!');
    } catch {
      // Submit failed
      toast.error('Failed to submit project');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white rounded-3xl p-8 max-w-md w-full text-center shadow-lg border border-gray-200"
        >
          <CheckCircle className="h-16 w-16 mx-auto text-mint mb-6" />
          <h1 className="text-2xl font-heading font-bold text-navy">Project Submitted!</h1>
          <p className="text-gray-500 mt-2">
            Thank you for your project request. Our design team will review it and get back to you within 24-48 hours.
          </p>
          <div className="flex gap-4 justify-center mt-6">
            <Button
              onClick={() => navigate('/dashboard/projects')}
              className="bg-mint hover:bg-mint-dark text-black rounded-full"
              data-testid="view-projects-btn"
            >
              View My Projects
            </Button>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <span className="text-sm text-coral uppercase tracking-[0.2em] font-semibold">
            Custom Design Service
          </span>
          <h1 className="mt-3 text-3xl sm:text-4xl font-heading font-bold text-navy">
            Bring Your Vision to Life
          </h1>
          <p className="mt-4 text-gray-600 max-w-2xl mx-auto">
            Describe your project and upload inspiration images. Our professional designers will create something amazing for you.
          </p>
        </motion.div>

        <form onSubmit={handleSubmit}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-2xl p-6 sm:p-8 border border-gray-200 space-y-6"
          >
            {/* Project Info */}
            <div className="grid sm:grid-cols-2 gap-6">
              <div className="sm:col-span-2">
                <Label htmlFor="title" className="text-navy font-medium">
                  Project Title *
                </Label>
                <Input
                  id="title"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  placeholder="e.g., Restaurant Menu Design"
                  className="mt-2"
                  required
                  data-testid="project-title-input"
                />
              </div>

              <div>
                <Label className="text-navy font-medium">Project Type *</Label>
                <Select
                  value={formData.project_type}
                  onValueChange={(val) => handleSelectChange('project_type', val)}
                >
                  <SelectTrigger className="mt-2" data-testid="project-type-select">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {projectTypes.map((type) => (
                      <SelectItem key={type} value={type}>
                        {type}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-navy font-medium">Budget Range</Label>
                <Select
                  value={formData.budget_range}
                  onValueChange={(val) => handleSelectChange('budget_range', val)}
                >
                  <SelectTrigger className="mt-2" data-testid="budget-range-select">
                    <SelectValue placeholder="Select budget" />
                  </SelectTrigger>
                  <SelectContent>
                    {budgetRanges.map((range) => (
                      <SelectItem key={range} value={range}>
                        {range}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="sm:col-span-2">
                <Label htmlFor="deadline" className="text-navy font-medium">
                  Deadline (Optional)
                </Label>
                <Input
                  id="deadline"
                  name="deadline"
                  type="date"
                  value={formData.deadline}
                  onChange={handleChange}
                  className="mt-2"
                  min={new Date().toISOString().split('T')[0]}
                  data-testid="deadline-input"
                />
              </div>
            </div>

            {/* Description */}
            <div>
              <Label htmlFor="description" className="text-navy font-medium">
                Project Description *
              </Label>
              <Textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder="Describe your project in detail. What do you need? What style are you looking for? Include any specific requirements, colors, fonts, or elements you want..."
                rows={6}
                className="mt-2"
                required
                data-testid="project-description-input"
              />
            </div>

            {/* Inspiration Images */}
            <div>
              <Label className="text-navy font-medium mb-3 block">
                <Palette className="h-4 w-4 inline mr-2" />
                Inspiration Images (Optional)
              </Label>
              <p className="text-sm text-gray-500 mb-4">
                {"Upload images that inspire you or show the style you're looking for"}
              </p>

              {/* Upload Area */}
              <div className="border-2 border-dashed border-gray-200 rounded-xl p-6 text-center hover:border-mint/50 transition-colors relative">
                <Upload className="h-8 w-8 mx-auto text-gray-400 mb-2" />
                <p className="text-sm text-gray-500">
                  {uploading ? 'Uploading...' : 'Click or drag images here'}
                </p>
                <p className="text-xs text-gray-400 mt-1">PNG, JPG up to 10MB each</p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleFileUpload}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                  disabled={uploading}
                  data-testid="inspiration-upload-input"
                />
              </div>

              {/* Uploaded Images Preview */}
              {inspirationImages.length > 0 && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-4">
                  {inspirationImages.map((img) => (
                    <div key={img.id} className="relative group">
                      <img
                        src={img.preview}
                        alt={img.name}
                        className="w-full aspect-square object-cover rounded-xl"
                      />
                      <button
                        type="button"
                        onClick={() => removeImage(img.id)}
                        className="absolute top-2 right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Submit */}
            <Button
              type="submit"
              disabled={submitting || !user}
              className="w-full bg-mint hover:bg-mint-dark text-black py-6 rounded-full btn-hover"
              data-testid="submit-project-btn"
            >
              {submitting ? 'Submitting...' : (user ? 'Submit Project' : 'Login to Submit')}
              {!submitting && <ArrowRight className="ml-2 h-4 w-4" />}
            </Button>

            {!user && (
              <p className="text-center text-sm text-gray-500">
                Please{' '}
                <a href="/login" className="text-mint hover:underline">
                  login
                </a>{' '}
                to submit a project
              </p>
            )}
          </motion.div>
        </form>
      </div>
    </div>
  );
}
